import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { readFlowState, writeFlowState } from './persistence'
import type {
  CoachmarkContextValue,
  CoachmarkFlow,
  CoachmarkFlowState,
  CoachmarkPersistedFlow,
} from './types'

const CoachmarkContext = createContext<CoachmarkContextValue | null>(null)

interface InternalFlowRecord {
  flow: CoachmarkFlow
  state: CoachmarkFlowState
  step: string | null
}

function hydrate(flow: CoachmarkFlow): InternalFlowRecord {
  const persisted = readFlowState(flow.id)
  if (!persisted || persisted.version !== flow.version) {
    return { flow, state: 'pending', step: null }
  }
  return { flow, state: persisted.state, step: persisted.step }
}

function persist(record: InternalFlowRecord): void {
  const payload: CoachmarkPersistedFlow = {
    state: record.state,
    step: record.step,
    version: record.flow.version,
  }
  writeFlowState(record.flow.id, payload)
}

export function CoachmarkProvider({ children }: { children: ReactNode }) {
  const [records, setRecords] = useState<Record<string, InternalFlowRecord>>({})
  // Mirror of the latest records used by the imperative API so callbacks
  // closing over `records` from a prior render see the up-to-date state.
  const recordsRef = useRef(records)
  recordsRef.current = records

  const updateRecord = useCallback(
    (flowId: string, updater: (current: InternalFlowRecord) => InternalFlowRecord) => {
      setRecords((current) => {
        const existing = current[flowId]
        if (!existing) return current
        const next = updater(existing)
        if (next === existing) return current
        persist(next)
        return { ...current, [flowId]: next }
      })
    },
    [],
  )

  const registerFlow = useCallback((flow: CoachmarkFlow) => {
    setRecords((current) => {
      if (current[flow.id]) return current
      const hydrated = hydrate(flow)
      return { ...current, [flow.id]: hydrated }
    })
  }, [])

  const start = useCallback(
    (flowId: string) => {
      updateRecord(flowId, (record) => {
        if (record.state === 'completed' || record.state === 'dismissed') return record
        if (record.state === 'active') return record
        return { ...record, state: 'active' }
      })
    },
    [updateRecord],
  )

  const advance = useCallback(
    (flowId: string) => {
      updateRecord(flowId, (record) => {
        if (record.state !== 'active' && record.state !== 'pending') return record
        const currentIndex = record.step
          ? record.flow.steps.findIndex((step) => step.id === record.step)
          : -1
        const nextIndex = currentIndex + 1
        if (nextIndex >= record.flow.steps.length) {
          return { ...record, state: 'completed', step: null }
        }
        return {
          ...record,
          state: 'active',
          step: record.flow.steps[nextIndex]!.id,
        }
      })
    },
    [updateRecord],
  )

  const skipStep = useCallback(
    (flowId: string, _stepId: string) => {
      // Skipping is identical to advancing today; the _stepId is reserved
      // for analytics callers that want to stamp which step was skipped.
      void _stepId
      advance(flowId)
    },
    [advance],
  )

  const dismissFlow = useCallback(
    (flowId: string) => {
      updateRecord(flowId, (record) => ({ ...record, state: 'dismissed', step: null }))
    },
    [updateRecord],
  )

  const getActiveStep = useCallback((flowId: string): string | null => {
    const record = recordsRef.current[flowId]
    if (!record) return null
    if (record.state !== 'active') return null
    return record.step
  }, [])

  const isCompleted = useCallback((flowId: string): boolean => {
    return recordsRef.current[flowId]?.state === 'completed'
  }, [])

  const isDismissed = useCallback((flowId: string): boolean => {
    return recordsRef.current[flowId]?.state === 'dismissed'
  }, [])

  const value = useMemo<CoachmarkContextValue>(
    () => ({
      registerFlow,
      start,
      advance,
      skipStep,
      dismissFlow,
      getActiveStep,
      isCompleted,
      isDismissed,
    }),
    [registerFlow, start, advance, skipStep, dismissFlow, getActiveStep, isCompleted, isDismissed],
  )

  return (
    <CoachmarkContext.Provider value={value}>
      <StateContext.Provider value={records}>{children}</StateContext.Provider>
    </CoachmarkContext.Provider>
  )
}

const StateContext = createContext<Record<string, InternalFlowRecord>>({})

export function useCoachmark(): CoachmarkContextValue {
  const value = useContext(CoachmarkContext)
  if (!value) {
    throw new Error('useCoachmark must be used inside a CoachmarkProvider')
  }
  return value
}

/**
 * Subscribes to a specific flow's active step id. Returns null when the
 * flow is not active. Components rendering a coachmark should anchor on
 * this hook so they re-render on `advance`/`skipStep`/`dismissFlow`.
 */
export function useActiveCoachmarkStep(flowId: string): string | null {
  const state = useContext(StateContext)
  const record = state[flowId]
  if (!record) return null
  if (record.state !== 'active') return null
  return record.step
}
